import * as React from 'react';
import {observer} from 'mobx-react';
import {classes, stylesheet} from 'typestyle';
import BuildingBlockHeader from './BuildingBlockHeader';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {px} from 'csx';
import {TProvider} from '../../../i18n/i18n';
import {BuildingBlockType, TextBuildingBlock} from '../../types/Report';
import {CiBlueColor, LightGreyColor, WhiteColor} from 'src/styles/colors';
import {REPORT_VARIABLES} from '../../stores/ReportEditorStore';

interface PaperSheetTextBlockProps {
    buildingBlock: TextBuildingBlock;
    removeBuildingBlock: (idBlock: string) => void;
    changeBuildingBlockType: (idBuildingBlock: string, newBlockType: BuildingBlockType) => void;
}

@observer
export default class PaperSheetTextBlock extends React.Component<PaperSheetTextBlockProps> {
    private textarea: HTMLTextAreaElement | null = null;

    private updateText = (e) => {
        this.props.buildingBlock.text = e.currentTarget.value;
    };

    private generateFontSizes(): React.ReactNode[] {
        const fontSizeOptions: React.ReactNode[] = [];
        let i;
        for (i = 0; i < 62; i++) {
            fontSizeOptions.push(
                <option key={i} value={i + 10}>
                    {i + 10}
                </option>
            );
        }
        return fontSizeOptions;
    }

    private toggleBold = () => {
        this.props.buildingBlock.textStyle.bold = !this.props.buildingBlock.textStyle.bold;
    };

    private toggleItalic = () => {
        this.props.buildingBlock.textStyle.italic = !this.props.buildingBlock.textStyle.italic;
    };

    private toggleUnderline = () => {
        this.props.buildingBlock.textStyle.underlined = !this.props.buildingBlock.textStyle.underlined;
    };

    private changeFontSize = (e) => {
        this.props.buildingBlock.textStyle.fontSize = parseInt(e.currentTarget.value, 10);
    };

    private onInsertReportVariable = (event: React.ChangeEvent<HTMLSelectElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!this.textarea) {
            return;
        }

        const tag = event.target.value;
        const isCustomTag = tag === 'custom';

        const currentText = this.props.buildingBlock.text;
        const selectionStart = this.textarea.selectionStart;
        const selectionEnd = this.textarea.selectionEnd;

        this.props.buildingBlock.text =
            currentText.substr(0, selectionStart) +
            '${' +
            (isCustomTag ? currentText.substr(selectionStart, selectionEnd - selectionStart) : tag) +
            '}' +
            currentText.substr(selectionEnd);

        window.setTimeout(() => {
            if (this.textarea) {
                const cursorPos = isCustomTag ? selectionEnd + 3 : selectionStart + tag.length + 3;
                this.textarea.selectionStart = cursorPos;
                this.textarea.selectionEnd = cursorPos;
                this.textarea.focus();
            }
        }, 50);
    };

    private renderTextStyleComponent(): React.ReactNode {
        const {fontSize, underlined, italic, bold} = this.props.buildingBlock.textStyle;

        const variables = ['custom', ...REPORT_VARIABLES];

        const reportVariableOptionNodes: React.ReactNode[] = [
            <TProvider key="_">
                {(t) => (
                    <option value="" className={textBlockCss.hiddenOption}>
                        {t('report:textBlockActions.variable')}
                    </option>
                )}
            </TProvider>,
        ];
        for (const variable of variables) {
            reportVariableOptionNodes.push(
                <TProvider key={variable}>
                    {(t) => (
                        <option value={variable} title={t('report:variableDescriptions:' + variable)}>
                            {'${'}
                            {variable}
                            {'}'}: {t('report:variables:' + variable)}
                        </option>
                    )}
                </TProvider>
            );
        }

        return (
            <TProvider>
                {(t) => (
                    <div className={textBlockCss.styleMenuContainer}>
                        <div
                            title={t('report:textBlockActions.bold')}
                            className={classes(textBlockCss.styleMenuBlock, bold ? textBlockCss.styleBlockActive : undefined)}
                            onClick={this.toggleBold}>
                            <FontAwesomeIcon icon="bold" />
                        </div>
                        <div
                            title={t('report:textBlockActions.italic')}
                            className={classes(textBlockCss.styleMenuBlock, italic ? textBlockCss.styleBlockActive : undefined)}
                            onClick={this.toggleItalic}>
                            <FontAwesomeIcon icon="italic" />
                        </div>
                        <div
                            title={t('report:textBlockActions.underline')}
                            className={classes(textBlockCss.styleMenuBlock, underlined ? textBlockCss.styleBlockActive : undefined)}
                            onClick={this.toggleUnderline}>
                            <FontAwesomeIcon icon="underline" />
                        </div>
                        <div className={textBlockCss.styleMenuBlock}>
                            <select className={textBlockCss.fontSizeSelect} value={fontSize} onChange={this.changeFontSize}>
                                {this.generateFontSizes()}
                            </select>
                        </div>
                        <div className={textBlockCss.styleMenuBlock}>
                            <select className={textBlockCss.fontSizeSelect} value={''} onChange={this.onInsertReportVariable}>
                                {reportVariableOptionNodes}
                            </select>
                        </div>
                    </div>
                )}
            </TProvider>
        );
    }

    public render() {
        const {buildingBlock, removeBuildingBlock} = this.props;
        const {textStyle, text} = buildingBlock;

        return (
            <div>
                <BuildingBlockHeader
                    idBuildingBlock={this.props.buildingBlock.id}
                    changeBuildingBlockType={this.props.changeBuildingBlockType}
                    additionalHeaderComponent={this.renderTextStyleComponent()}
                    removeBuildingBlock={() => removeBuildingBlock(buildingBlock.id)}
                    buildingBlockType={buildingBlock.blockType}
                />
                <div className={textBlockCss.textAreaContainer}>
                    <TProvider>
                        {(t) => (
                            <textarea
                                ref={(ref) => {
                                    this.textarea = ref;
                                }}
                                style={{
                                    fontSize: textStyle.fontSize + 5,
                                    fontWeight: textStyle.bold ? 'bold' : 'normal',
                                    fontStyle: textStyle.italic ? 'italic' : 'normal',
                                    textDecoration: textStyle.underlined ? 'underline' : 'none',
                                }}
                                value={text}
                                className={textBlockCss.typingArea}
                                onChange={this.updateText}
                                placeholder={t('text.typeHere')}
                            />
                        )}
                    </TProvider>
                </div>
            </div>
        );
    }
}

const textBlockCss = stylesheet({
    fontSizeSelect: {
        backgroundColor: 'transparent',
        height: '100%',
        border: 'none',
        outline: 'none',
    },
    styleBlockActive: {
        backgroundColor: CiBlueColor,
        color: WhiteColor,
    },
    styleMenuBlock: {
        cursor: 'pointer',
        fontWeight: 'bold',
        borderLeft: '1px solid ' + LightGreyColor,
        minWidth: 30,
        height: '100%',
        textAlign: 'center',
        lineHeight: px(30),
        paddingLeft: 10,
        paddingRight: 10,
    },
    styleMenuContainer: {
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        borderRight: '1px solid ' + LightGreyColor,
    },
    typingArea: {
        backgroundColor: 'transparent',
        width: '100%',
        padding: 15,
        textAlign: 'left',
        cursor: 'text',
        minHeight: 50,
        border: 'none',
        flexGrow: 1,
    },
    textAreaContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    hiddenOption: {
        display: 'none',
    },
});



// WEBPACK FOOTER //
// ./src/report/views/PaperSheetBlocks/PaperSheetTextBlock.tsx